import React, { useEffect, useMemo, useState } from 'react';
import { Row, Col, Button, Form, Spinner } from 'react-bootstrap';
import { PencilFill, CheckLg, XLg, PlusCircle, Trash } from 'react-bootstrap-icons';
import { useAuth } from '../../contexts/SecurityContext';
import RoleGuard from '../security/RoleGuard';
import SectionControls from '../SectionControls';
import { updateChallengeTimelineSection } from '../../services/contentService';
import './ChallengeTimelineBlock.scss';

const STATE_OPTIONS = [
  { value: 'completed', label: 'Completed (filled red dot)' },
  { value: 'current', label: 'Current (hollow red ring)' },
  { value: 'future', label: 'Future (hollow grey ring)' },
];

const STATUS_COLOR_OPTIONS = [
  { value: 'red', label: 'Red' },
  { value: 'green', label: 'Green' },
  { value: 'grey', label: 'Grey' },
  { value: 'black', label: 'Black' },
];

function defaultItem(state = 'future') {
  return {
    date: 'Month YYYY',
    title: 'Milestone',
    description: 'Short description of this milestone.',
    state,
  };
}

export function defaultChallengeTimelineData() {
  return {
    title: 'Challenge timeline',
    items: [
      defaultItem('completed'),
      defaultItem('completed'),
      defaultItem('current'),
      defaultItem('future'),
    ],
    statusLabel: 'STATUS',
    statusValue: 'Under review',
    statusColor: 'red',
    durationLabel: 'DURATION',
    durationValue: '8 months',
    durationColor: 'black',
  };
}

export function ChallengeTimelineView({ data }) {
  const items = Array.isArray(data?.items) ? data.items : [];
  return (
    <div className="challenge-timeline-block">
      {data?.title ? <h2 className="challenge-timeline-block__title">{data.title}</h2> : null}
      <Row className="g-4">
        <Col md={8}>
          <ol className="challenge-timeline-block__list">
            {items.map((item, idx) => {
              const state = item.state || 'future';
              const isLast = idx === items.length - 1;
              return (
                <li
                  key={idx}
                  className={`challenge-timeline-block__item challenge-timeline-block__item--${state} ${isLast ? 'challenge-timeline-block__item--last' : ''}`}
                >
                  <div className="challenge-timeline-block__marker">
                    <span className="challenge-timeline-block__dot" />
                  </div>
                  <div className="challenge-timeline-block__content">
                    {item.date ? (
                      <div className="challenge-timeline-block__date">{item.date}</div>
                    ) : null}
                    {item.title ? (
                      <div className="challenge-timeline-block__item-title">{item.title}</div>
                    ) : null}
                    {item.description ? (
                      <div className="challenge-timeline-block__description">{item.description}</div>
                    ) : null}
                  </div>
                </li>
              );
            })}
          </ol>
        </Col>
        <Col md={4}>
          {data?.statusValue ? (
            <div className="challenge-timeline-block__side-card">
              <div className="challenge-timeline-block__side-label">{data.statusLabel || 'STATUS'}</div>
              <div className={`challenge-timeline-block__side-value challenge-timeline-block__side-value--${data.statusColor || 'red'}`}>
                {data.statusValue}
              </div>
            </div>
          ) : null}
          {data?.durationValue ? (
            <div className="challenge-timeline-block__side-card">
              <div className="challenge-timeline-block__side-label">{data.durationLabel || 'DURATION'}</div>
              <div className={`challenge-timeline-block__side-value challenge-timeline-block__side-value--${data.durationColor || 'black'}`}>
                {data.durationValue}
              </div>
            </div>
          ) : null}
        </Col>
      </Row>
    </div>
  );
}

function ChallengeTimelineBlock({
  pageId, section, onMoveUp, onMoveDown, onDelete, isFirstSection, isLastSection,
}) {
  const { user } = useAuth();
  const initialData = useMemo(() => section?.data || defaultChallengeTimelineData(), [section]);
  const [data, setData] = useState(initialData);
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => { setData(section?.data || defaultChallengeTimelineData()); }, [section]);

  async function handleSave() {
    setIsSaving(true);
    try {
      const modifiedBy = user?.attributes?.email || user?.username || 'user';
      await updateChallengeTimelineSection(pageId, section.position, data, modifiedBy);
      setIsEditing(false);
    } catch (e) {
      console.error('Error saving challenge timeline:', e);
      // eslint-disable-next-line no-alert
      alert('Failed to save section.');
    } finally {
      setIsSaving(false);
    }
  }

  function handleCancel() { setData(section?.data || defaultChallengeTimelineData()); setIsEditing(false); }
  function updateField(field, value) { setData((prev) => ({ ...prev, [field]: value })); }
  function updateItem(idx, patch) {
    setData((prev) => {
      const next = { ...prev, items: [...(prev.items || [])] };
      next.items[idx] = { ...next.items[idx], ...patch };
      return next;
    });
  }
  function addItem() { setData((prev) => ({ ...prev, items: [...(prev.items || []), defaultItem('future')] })); }
  function removeItem(idx) {
    setData((prev) => {
      const next = { ...prev, items: [...(prev.items || [])] };
      next.items.splice(idx, 1);
      return next;
    });
  }

  if (isEditing) {
    return (
      <div className="challenge-timeline-block challenge-timeline-block--editing">
        <Form.Group className="mb-3">
          <Form.Label>Section title</Form.Label>
          <Form.Control type="text" value={data.title || ''} onChange={(e) => updateField('title', e.target.value)} />
        </Form.Group>

        <h6 className="challenge-timeline-block__editor-section-heading">Timeline items</h6>
        {(data.items || []).map((item, idx) => (
          <div key={idx} className="challenge-timeline-block__editor-item">
            <div className="d-flex justify-content-between align-items-center mb-2">
              <strong>Item {idx + 1}</strong>
              <Button variant="outline-danger" size="sm" onClick={() => removeItem(idx)}>
                <Trash /> Remove
              </Button>
            </div>
            <Row className="g-2">
              <Col md={4}>
                <Form.Label>Date</Form.Label>
                <Form.Control type="text" placeholder="December 2025" value={item.date || ''} onChange={(e) => updateItem(idx, { date: e.target.value })} />
              </Col>
              <Col md={4}>
                <Form.Label>Title</Form.Label>
                <Form.Control type="text" value={item.title || ''} onChange={(e) => updateItem(idx, { title: e.target.value })} />
              </Col>
              <Col md={4}>
                <Form.Label>State</Form.Label>
                <Form.Select value={item.state || 'future'} onChange={(e) => updateItem(idx, { state: e.target.value })}>
                  {STATE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                </Form.Select>
              </Col>
              <Col md={12}>
                <Form.Label>Description</Form.Label>
                <Form.Control as="textarea" rows={2} value={item.description || ''} onChange={(e) => updateItem(idx, { description: e.target.value })} />
              </Col>
            </Row>
          </div>
        ))}
        <Button variant="outline-primary" size="sm" onClick={addItem} className="mb-3">
          <PlusCircle /> Add item
        </Button>

        <h6 className="challenge-timeline-block__editor-section-heading">Side cards</h6>
        <Row className="g-2 mb-2">
          <Col md={4}>
            <Form.Label>Status label</Form.Label>
            <Form.Control type="text" value={data.statusLabel || ''} onChange={(e) => updateField('statusLabel', e.target.value)} />
          </Col>
          <Col md={5}>
            <Form.Label>Status value</Form.Label>
            <Form.Control type="text" value={data.statusValue || ''} onChange={(e) => updateField('statusValue', e.target.value)} />
          </Col>
          <Col md={3}>
            <Form.Label>Status colour</Form.Label>
            <Form.Select value={data.statusColor || 'red'} onChange={(e) => updateField('statusColor', e.target.value)}>
              {STATUS_COLOR_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </Form.Select>
          </Col>
          <Col md={4}>
            <Form.Label>Duration label</Form.Label>
            <Form.Control type="text" value={data.durationLabel || ''} onChange={(e) => updateField('durationLabel', e.target.value)} />
          </Col>
          <Col md={5}>
            <Form.Label>Duration value</Form.Label>
            <Form.Control type="text" value={data.durationValue || ''} onChange={(e) => updateField('durationValue', e.target.value)} />
          </Col>
          <Col md={3}>
            <Form.Label>Duration colour</Form.Label>
            <Form.Select value={data.durationColor || 'black'} onChange={(e) => updateField('durationColor', e.target.value)}>
              {STATUS_COLOR_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </Form.Select>
          </Col>
        </Row>

        <div className="challenge-timeline-block__editor-actions">
          <Button variant="success" onClick={handleSave} disabled={isSaving}>
            {isSaving ? (<><Spinner as="span" animation="border" size="sm" className="me-2" />Saving…</>) : (<><CheckLg /> Save</>)}
          </Button>
          <Button variant="secondary" onClick={handleCancel} disabled={isSaving}><XLg /> Cancel</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="challenge-timeline-block-wrapper">
      <ChallengeTimelineView data={data} />
      <RoleGuard role="admin">
        <div className="admin-controls">
          <Button variant="outline-secondary" size="sm" className="edit-btn" onClick={() => setIsEditing(true)}>
            <PencilFill /> Edit timeline
          </Button>
          <SectionControls onMoveUp={onMoveUp} onMoveDown={onMoveDown} onDelete={onDelete} isFirstSection={isFirstSection} isLastSection={isLastSection} />
        </div>
      </RoleGuard>
    </div>
  );
}

export default ChallengeTimelineBlock;
